import React from "react";

/**
 * Framework diagram primitives — matched to the user's reference style:
 *   • Pale cream bg                  #FDFBF5
 *   • Gold outline/lines             #C9A961
 *   • Highlighted fill               #F5E5A8 (deeper warm cream)
 *   • Unhighlighted / muted fill     #FBF0EE (very pale pink)
 *   • Bold black labels              #111111
 *   • Subtle gray axis labels        #9CA3AF (tracked uppercase)
 */

const COLORS = {
  gold: "#14110F",            // ink — primary stroke (1px hairline feel)
  goldSoft: "#D94B2A",        // vermilion — used sparingly for center labels + accent numerals
  highlight: "#F4D6C9",       // vermilion at ~20% over bone — highlighted cells
  muted: "#E6DECC",           // bone-soft — unhighlighted cells
  surface: "#EDE7DA",         // bone — chart background
  axis: "rgba(20,17,15,0.30)",
  axisStrong: "rgba(20,17,15,0.55)",
  label: "#14110F",
};

const FONT_LABEL = '700 14px "IBM Plex Sans", system-ui, sans-serif';
const FONT_AXIS = '500 11px "IBM Plex Sans", system-ui, sans-serif';
const FONT_AXIS_TITLE = '600 12px "IBM Plex Sans", system-ui, sans-serif';

/* ------------------------------------------------------------------ */
/* SHARED HELPERS                                                      */
/* ------------------------------------------------------------------ */

function wrapLines(text, maxCharsPerLine = 16) {
  if (!text) return [""];
  const words = String(text).split(/\s+/);
  const lines = [];
  let current = "";
  for (const w of words) {
    if (!current) {
      current = w;
    } else if ((current + " " + w).length <= maxCharsPerLine) {
      current += " " + w;
    } else {
      lines.push(current);
      current = w;
    }
  }
  if (current) lines.push(current);
  return lines.slice(0, 3);
}

function MultilineText({ x, y, text, maxChars = 16, lineHeight = 18, fill = COLORS.label, font = FONT_LABEL, anchor = "middle" }) {
  const lines = wrapLines(text, maxChars);
  const startDy = -((lines.length - 1) * lineHeight) / 2;
  return (
    <text x={x} y={y} textAnchor={anchor} style={{ font, fill }}>
      {lines.map((line, i) => (
        <tspan key={i} x={x} dy={i === 0 ? startDy : lineHeight}>
          {line}
        </tspan>
      ))}
    </text>
  );
}

/* ------------------------------------------------------------------ */
/* QUADRANT (2x2 Matrix)                                               */
/* ------------------------------------------------------------------ */

export function QuadrantDiagram({ data }) {
  const {
    x_axis = "X AXIS",
    y_axis = "Y AXIS",
    x_low = "Low",
    x_high = "High",
    y_low = "Low",
    y_high = "High",
    quadrants = [],
  } = data || {};

  const W = 720, H = 560;
  const PAD_L = 130, PAD_R = 40, PAD_T = 40, PAD_B = 90;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const midX = PAD_L + innerW / 2;
  const midY = PAD_T + innerH / 2;

  const posMap = {
    top_left: { x: PAD_L + innerW * 0.25, y: PAD_T + innerH * 0.25 },
    top_right: { x: PAD_L + innerW * 0.75, y: PAD_T + innerH * 0.25 },
    bottom_left: { x: PAD_L + innerW * 0.25, y: PAD_T + innerH * 0.75 },
    bottom_right: { x: PAD_L + innerW * 0.75, y: PAD_T + innerH * 0.75 },
  };

  const rectMap = {
    top_left: { x: PAD_L, y: PAD_T, w: innerW / 2, h: innerH / 2 },
    top_right: { x: PAD_L + innerW / 2, y: PAD_T, w: innerW / 2, h: innerH / 2 },
    bottom_left: { x: PAD_L, y: PAD_T + innerH / 2, w: innerW / 2, h: innerH / 2 },
    bottom_right: { x: PAD_L + innerW / 2, y: PAD_T + innerH / 2, w: innerW / 2, h: innerH / 2 },
  };

  return (
    <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ maxWidth: "100%", height: "auto" }}>
      {/* Quadrant fills */}
      {quadrants.map((q) => {
        const r = rectMap[q.position];
        if (!r) return null;
        return (
          <rect
            key={q.position}
            x={r.x}
            y={r.y}
            width={r.w}
            height={r.h}
            fill={q.highlighted ? COLORS.highlight : COLORS.muted}
          />
        );
      })}

      {/* Quadrant borders (gold) */}
      <rect x={PAD_L} y={PAD_T} width={innerW} height={innerH} fill="none" stroke={COLORS.gold} strokeWidth="2" />
      <line x1={midX} y1={PAD_T} x2={midX} y2={PAD_T + innerH} stroke={COLORS.gold} strokeWidth="2" />
      <line x1={PAD_L} y1={midY} x2={PAD_L + innerW} y2={midY} stroke={COLORS.gold} strokeWidth="2" />

      {/* Quadrant labels */}
      {quadrants.map((q) => {
        const p = posMap[q.position];
        if (!p) return null;
        return <MultilineText key={`l-${q.position}`} x={p.x} y={p.y} text={q.label} maxChars={14} />;
      })}

      {/* Y axis arrow + High/Low */}
      <defs>
        <marker id="arrow-axis" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="8" markerHeight="8" orient="auto">
          <path d="M0,0 L10,5 L0,10 z" fill={COLORS.axisStrong} />
        </marker>
      </defs>
      <line x1={PAD_L} y1={PAD_T + innerH} x2={PAD_L} y2={PAD_T - 18} stroke={COLORS.axisStrong} strokeWidth="1.5" markerEnd="url(#arrow-axis)" />
      <text x={PAD_L + 6} y={PAD_T + 8} style={{ font: FONT_AXIS, fill: COLORS.axis }}>{y_high}</text>
      <text x={PAD_L + 6} y={PAD_T + innerH - 4} style={{ font: FONT_AXIS, fill: COLORS.axis }}>{y_low}</text>

      {/* X axis arrow + Low/High */}
      <line x1={PAD_L} y1={PAD_T + innerH} x2={PAD_L + innerW + 18} y2={PAD_T + innerH} stroke={COLORS.axisStrong} strokeWidth="1.5" markerEnd="url(#arrow-axis)" />
      <text x={PAD_L} y={PAD_T + innerH + 22} style={{ font: FONT_AXIS, fill: COLORS.axis }}>{x_low}</text>
      <text x={PAD_L + innerW} y={PAD_T + innerH + 22} textAnchor="end" style={{ font: FONT_AXIS, fill: COLORS.axis }}>{x_high}</text>

      {/* Axis titles */}
      <text
        x={20}
        y={midY}
        textAnchor="middle"
        transform={`rotate(-90, 20, ${midY})`}
        style={{ font: FONT_AXIS_TITLE, fill: COLORS.axis, letterSpacing: "0.15em" }}
      >
        {String(y_axis).toUpperCase()}
      </text>
      <text x={midX} y={H - 18} textAnchor="middle" style={{ font: FONT_AXIS_TITLE, fill: COLORS.axis, letterSpacing: "0.15em" }}>
        {String(x_axis).toUpperCase()}
      </text>
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* TRIANGLE (3 corners + center sweet spot)                            */
/* ------------------------------------------------------------------ */

export function TriangleDiagram({ data }) {
  const { nodes = [], center = "" } = data || {};
  const n = nodes.slice(0, 3);
  const W = 640, H = 560;
  const cx = W / 2, cy = H / 2;
  const R = 200;
  // Top, bottom-left, bottom-right
  const positions = [
    { x: cx, y: cy - R + 30 },
    { x: cx - R * Math.sin(Math.PI / 3), y: cy + R * Math.cos(Math.PI / 3) + 30 },
    { x: cx + R * Math.sin(Math.PI / 3), y: cy + R * Math.cos(Math.PI / 3) + 30 },
  ];
  const nodeR = 64;

  // Triangle path between the nodes (their edges)
  const trianglePath = positions.map((p) => `${p.x},${p.y}`).join(" ");

  return (
    <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ maxWidth: "100%", height: "auto" }}>
      {/* Filled triangle background */}
      <polygon points={trianglePath} fill={COLORS.muted} stroke={COLORS.gold} strokeWidth="2.5" />
      {/* Center label */}
      <text x={cx} y={cy + 40} textAnchor="middle" style={{ font: '600 18px "IBM Plex Sans", sans-serif', fill: COLORS.goldSoft, fontStyle: "italic" }}>
        {center}
      </text>
      {/* Corner nodes */}
      {n.map((label, i) => (
        <g key={i}>
          <circle cx={positions[i].x} cy={positions[i].y} r={nodeR} fill={COLORS.highlight} stroke={COLORS.gold} strokeWidth="2.5" />
          <MultilineText x={positions[i].x} y={positions[i].y} text={label} maxChars={12} lineHeight={16} />
        </g>
      ))}
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* HUB & SPOKE (Center + N surrounding nodes)                          */
/* ------------------------------------------------------------------ */

export function HubSpokeDiagram({ data }) {
  const { center = "", spokes = [] } = data || {};
  const W = 760, H = 620;
  const cx = W / 2, cy = H / 2;
  const ringR = 230;
  const nodeR = 72;
  const centerR = 92;
  const N = spokes.length || 1;

  const points = spokes.map((_, i) => {
    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / N;
    return {
      x: cx + ringR * Math.cos(angle),
      y: cy + ringR * Math.sin(angle),
    };
  });

  return (
    <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ maxWidth: "100%", height: "auto" }}>
      {/* Spoke lines (drawn to edge of center circle, behind nodes) */}
      {points.map((p, i) => {
        const dx = p.x - cx, dy = p.y - cy;
        const len = Math.sqrt(dx * dx + dy * dy) || 1;
        const ux = dx / len, uy = dy / len;
        return (
          <line
            key={i}
            x1={cx + ux * (centerR - 2)}
            y1={cy + uy * (centerR - 2)}
            x2={p.x - ux * (nodeR - 2)}
            y2={p.y - uy * (nodeR - 2)}
            stroke={COLORS.gold}
            strokeWidth="1.75"
          />
        );
      })}
      {/* Center node */}
      <circle cx={cx} cy={cy} r={centerR} fill={COLORS.highlight} stroke={COLORS.gold} strokeWidth="3" />
      <MultilineText
        x={cx}
        y={cy}
        text={center}
        maxChars={11}
        lineHeight={20}
        fill={COLORS.goldSoft}
        font='700 18px "IBM Plex Sans", sans-serif'
      />
      {/* Spoke nodes */}
      {spokes.map((label, i) => (
        <g key={i}>
          <circle cx={points[i].x} cy={points[i].y} r={nodeR} fill={COLORS.muted} stroke={COLORS.gold} strokeWidth="2.25" />
          <MultilineText
            x={points[i].x}
            y={points[i].y}
            text={label}
            maxChars={12}
            lineHeight={16}
            font='500 15px "IBM Plex Sans", sans-serif'
          />
        </g>
      ))}
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* CHAIN (numbered vertical rounded-top boxes — value chain / journey)  */
/* ------------------------------------------------------------------ */

export function ChainDiagram({ data }) {
  const steps = (data?.steps || []).slice(0, 8);
  const N = steps.length || 1;
  const gap = 22;
  const boxW = N >= 7 ? 110 : 130;
  const boxH = N >= 7 ? 250 : 280;
  const padX = 26, padY = 26;
  const W = padX * 2 + N * boxW + (N - 1) * gap;
  const H = padY * 2 + boxH;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ maxWidth: "100%", height: "auto" }}>
      {steps.map((label, i) => {
        const x = padX + i * (boxW + gap);
        const y = padY;
        const numY = y + 70;
        const labelY = y + boxH - 50;
        return (
          <g key={i}>
            <rect
              x={x}
              y={y}
              width={boxW}
              height={boxH}
              rx="14"
              ry="14"
              fill={COLORS.muted}
              stroke={COLORS.gold}
              strokeWidth="2.5"
            />
            <text
              x={x + boxW / 2}
              y={numY}
              textAnchor="middle"
              style={{ font: '700 36px "IBM Plex Sans", sans-serif', fill: COLORS.goldSoft }}
            >
              {i + 1}
            </text>
            <MultilineText
              x={x + boxW / 2}
              y={labelY}
              text={label}
              maxChars={12}
              lineHeight={18}
              font='600 15px "IBM Plex Sans", sans-serif'
            />
            {/* Hairline connector to the next box */}
            {i < N - 1 && (
              <g>
                <line
                  x1={x + boxW}
                  y1={H / 2}
                  x2={x + boxW + gap}
                  y2={H / 2}
                  stroke={COLORS.gold}
                  strokeWidth="1.5"
                />
                <line
                  x1={x + boxW + gap / 2}
                  y1={H / 2 - 7}
                  x2={x + boxW + gap / 2}
                  y2={H / 2 + 7}
                  stroke={COLORS.gold}
                  strokeWidth="1.5"
                />
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* TREE (top-down hierarchy — root + branches)                         */
/* ------------------------------------------------------------------ */

export function TreeDiagram({ data }) {
  const { root = "", branches = [] } = data || {};
  const N = branches.length || 1;
  const W = Math.max(720, N * 180);
  const H = 360;
  const cx = W / 2;
  const rootY = 60;
  const rootW = 220, rootH = 72;
  const childY = 220;
  const childW = Math.min(170, (W - 60) / N - 20);
  const childH = 64;
  const childSpacing = (W - 60) / N;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ maxWidth: "100%", height: "auto" }}>
      {/* Root */}
      <rect x={cx - rootW / 2} y={rootY} width={rootW} height={rootH} fill={COLORS.highlight} stroke={COLORS.gold} strokeWidth="2.5" rx="4" />
      <MultilineText x={cx} y={rootY + rootH / 2} text={root} maxChars={20} lineHeight={16} />
      {/* Branches */}
      {branches.map((b, i) => {
        const bx = 30 + i * childSpacing + (childSpacing - childW) / 2;
        const label = typeof b === "string" ? b : b?.label || "";
        return (
          <g key={i}>
            <line x1={cx} y1={rootY + rootH} x2={bx + childW / 2} y2={childY} stroke={COLORS.gold} strokeWidth="1.5" />
            <rect x={bx} y={childY} width={childW} height={childH} fill={COLORS.muted} stroke={COLORS.gold} strokeWidth="2" rx="4" />
            <MultilineText x={bx + childW / 2} y={childY + childH / 2} text={label} maxChars={16} lineHeight={14} font='600 12px "IBM Plex Sans", sans-serif' />
          </g>
        );
      })}
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* FUNNEL                                                              */
/* ------------------------------------------------------------------ */

export function FunnelDiagram({ data }) {
  const stages = (data?.stages || []).slice(0, 6);
  const N = stages.length || 1;
  const W = 640, H = 60 + N * 70;
  const top = 30, bottom = H - 30;
  const topW = 560, bottomW = 180;
  const stepH = (bottom - top) / N;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ maxWidth: "100%", height: "auto" }}>
      {stages.map((label, i) => {
        const y0 = top + i * stepH;
        const y1 = y0 + stepH;
        const w0 = topW - ((topW - bottomW) * i) / N;
        const w1 = topW - ((topW - bottomW) * (i + 1)) / N;
        const pts = `${(W - w0) / 2},${y0} ${(W + w0) / 2},${y0} ${(W + w1) / 2},${y1} ${(W - w1) / 2},${y1}`;
        const highlighted = i === N - 1;
        return (
          <g key={i}>
            <polygon points={pts} fill={highlighted ? COLORS.highlight : COLORS.muted} stroke={COLORS.gold} strokeWidth="2" />
            <MultilineText x={W / 2} y={(y0 + y1) / 2} text={label} maxChars={28 - i * 2} lineHeight={15} font='600 13px "IBM Plex Sans", sans-serif' />
          </g>
        );
      })}
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* LAYERS (iceberg / horizontal stacked bands)                         */
/* ------------------------------------------------------------------ */

export function LayersDiagram({ data }) {
  const layers = (data?.layers || []).slice(0, 6);
  const threshold = typeof data?.below_threshold_index === "number" ? data.below_threshold_index : -1;
  const N = layers.length || 1;
  const W = 640, padL = 32, padR = 32, padT = 30, padB = 30;
  const bandH = 64;
  const H = padT + padB + N * bandH;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ maxWidth: "100%", height: "auto" }}>
      {layers.map((label, i) => {
        const y = padT + i * bandH;
        const highlighted = threshold >= 0 ? i >= threshold : i === N - 1;
        return (
          <g key={i}>
            <rect x={padL} y={y} width={W - padL - padR} height={bandH - 6} fill={highlighted ? COLORS.highlight : COLORS.muted} stroke={COLORS.gold} strokeWidth="2" />
            <MultilineText x={W / 2} y={y + (bandH - 6) / 2} text={label} maxChars={36} lineHeight={15} font='600 13px "IBM Plex Sans", sans-serif' />
          </g>
        );
      })}
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* CYCLE (circular arrows around a center label)                       */
/* ------------------------------------------------------------------ */

export function CycleDiagram({ data }) {
  const { steps = [], center = "" } = data || {};
  const N = steps.length || 1;
  const W = 640, H = 560;
  const cx = W / 2, cy = H / 2;
  const ringR = 200;
  const nodeR = 60;

  const positions = steps.map((_, i) => {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / N;
    return { x: cx + ringR * Math.cos(a), y: cy + ringR * Math.sin(a) };
  });

  // Curved arrows between consecutive nodes
  const arrows = positions.map((p, i) => {
    const next = positions[(i + 1) % N];
    // Compute angle from p to next
    const dx = next.x - p.x;
    const dy = next.y - p.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    const ux = dx / len, uy = dy / len;
    const startX = p.x + ux * nodeR;
    const startY = p.y + uy * nodeR;
    const endX = next.x - ux * nodeR;
    const endY = next.y - uy * nodeR;
    // Control point pushed outward from center to bow the arrow
    const midX = (startX + endX) / 2;
    const midY = (startY + endY) / 2;
    const outX = midX - cx;
    const outY = midY - cy;
    const outLen = Math.sqrt(outX * outX + outY * outY) || 1;
    const ctrlX = midX + (outX / outLen) * 40;
    const ctrlY = midY + (outY / outLen) * 40;
    return { startX, startY, endX, endY, ctrlX, ctrlY };
  });

  return (
    <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ maxWidth: "100%", height: "auto" }}>
      <defs>
        <marker id="cycle-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto">
          <path d="M0,0 L10,5 L0,10 z" fill={COLORS.gold} />
        </marker>
      </defs>
      {/* Arrows */}
      {arrows.map((a, i) => (
        <path
          key={`a-${i}`}
          d={`M ${a.startX} ${a.startY} Q ${a.ctrlX} ${a.ctrlY} ${a.endX} ${a.endY}`}
          fill="none"
          stroke={COLORS.gold}
          strokeWidth="1.75"
          markerEnd="url(#cycle-arrow)"
        />
      ))}
      {/* Center label */}
      {center && (
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" style={{ font: '600 18px "IBM Plex Sans", sans-serif', fill: COLORS.goldSoft, fontStyle: "italic" }}>
          {center}
        </text>
      )}
      {/* Step nodes */}
      {steps.map((label, i) => (
        <g key={`n-${i}`}>
          <circle cx={positions[i].x} cy={positions[i].y} r={nodeR} fill={COLORS.highlight} stroke={COLORS.gold} strokeWidth="2.5" />
          <MultilineText x={positions[i].x} y={positions[i].y} text={label} maxChars={12} lineHeight={15} font='600 13px "IBM Plex Sans", sans-serif' />
        </g>
      ))}
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* PYRAMID (stacked rectangles widening downward — root-cause cascade)  */
/* ------------------------------------------------------------------ */

export function PyramidDiagram({ data }) {
  const tiers = (data?.tiers || data?.layers || []).slice(0, 6);
  const alternate = !!data?.alternate_highlight;
  const N = tiers.length || 1;
  const W = 720;
  const padTop = 30, padBottom = 30, tierH = 90, gap = 14;
  const H = padTop + padBottom + N * tierH + (N - 1) * gap;
  const minW = 220, maxW = W - 80;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ maxWidth: "100%", height: "auto" }}>
      {tiers.map((label, i) => {
        const tierWidth = minW + ((maxW - minW) * i) / Math.max(1, N - 1);
        const x = (W - tierWidth) / 2;
        const y = padTop + i * (tierH + gap);
        // If alternating, highlight every other tier starting from index 0
        const highlighted = alternate ? i % 2 === 0 : true;
        return (
          <g key={i}>
            <rect
              x={x}
              y={y}
              width={tierWidth}
              height={tierH}
              rx="4"
              ry="4"
              fill={highlighted ? COLORS.highlight : COLORS.muted}
              stroke={COLORS.gold}
              strokeWidth="2.5"
            />
            <MultilineText
              x={W / 2}
              y={y + tierH / 2}
              text={label}
              maxChars={36}
              lineHeight={18}
              font='700 16px "IBM Plex Sans", sans-serif'
            />
          </g>
        );
      })}
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* MATRIX (labeled grid with row & column headers — BMC, RACI, etc.)   */
/* ------------------------------------------------------------------ */

export function MatrixDiagram({ data }) {
  const columns = (data?.columns || []).slice(0, 5);
  const rows = (data?.rows || []).slice(0, 5);
  const cells = data?.cells || [];
  const C = columns.length || 1;
  const R = rows.length || 1;

  const cellW = 160;
  const cellH = 110;
  const rowHeaderW = 150;
  const padX = 24, padY = 24;
  const W = padX * 2 + rowHeaderW + C * cellW;
  const H = padY * 2 + cellH + R * cellH;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ maxWidth: "100%", height: "auto" }}>
      {/* Empty top-left corner */}
      <rect x={padX} y={padY} width={rowHeaderW} height={cellH} fill={COLORS.highlight} stroke={COLORS.gold} strokeWidth="2" />
      {/* Column headers */}
      {columns.map((col, i) => (
        <g key={`c-${i}`}>
          <rect
            x={padX + rowHeaderW + i * cellW}
            y={padY}
            width={cellW}
            height={cellH}
            fill={COLORS.highlight}
            stroke={COLORS.gold}
            strokeWidth="2"
          />
          <MultilineText
            x={padX + rowHeaderW + i * cellW + cellW / 2}
            y={padY + cellH / 2}
            text={col}
            maxChars={14}
            lineHeight={18}
            font='700 16px "IBM Plex Sans", sans-serif'
          />
        </g>
      ))}
      {/* Row headers + cells */}
      {rows.map((row, r) => (
        <g key={`r-${r}`}>
          <rect
            x={padX}
            y={padY + (r + 1) * cellH}
            width={rowHeaderW}
            height={cellH}
            fill={COLORS.highlight}
            stroke={COLORS.gold}
            strokeWidth="2"
          />
          <MultilineText
            x={padX + rowHeaderW / 2}
            y={padY + (r + 1) * cellH + cellH / 2}
            text={row}
            maxChars={14}
            lineHeight={18}
            font='700 15px "IBM Plex Sans", sans-serif'
          />
          {columns.map((_, c) => (
            <g key={`cell-${r}-${c}`}>
              <rect
                x={padX + rowHeaderW + c * cellW}
                y={padY + (r + 1) * cellH}
                width={cellW}
                height={cellH}
                fill={COLORS.muted}
                stroke={COLORS.gold}
                strokeWidth="2"
              />
              {cells?.[r]?.[c] && (
                <MultilineText
                  x={padX + rowHeaderW + c * cellW + cellW / 2}
                  y={padY + (r + 1) * cellH + cellH / 2}
                  text={cells[r][c]}
                  maxChars={16}
                  lineHeight={16}
                  font='500 13px "IBM Plex Sans", sans-serif'
                />
              )}
            </g>
          ))}
        </g>
      ))}
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* VENN (two overlapping ovals with intersection label)                */
/* ------------------------------------------------------------------ */

export function VennDiagram({ data }) {
  const { left = "Set A", right = "Set B", intersection = "" } = data || {};
  const W = 720, H = 480;
  const cx = W / 2, cy = H / 2;
  const rx = 180, ry = 220;
  const offset = 110;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ maxWidth: "100%", height: "auto" }}>
      <defs>
        <clipPath id="venn-left">
          <ellipse cx={cx - offset} cy={cy} rx={rx} ry={ry} />
        </clipPath>
        <clipPath id="venn-right">
          <ellipse cx={cx + offset} cy={cy} rx={rx} ry={ry} />
        </clipPath>
      </defs>
      {/* Left oval fill */}
      <ellipse cx={cx - offset} cy={cy} rx={rx} ry={ry} fill={COLORS.muted} />
      {/* Right oval fill */}
      <ellipse cx={cx + offset} cy={cy} rx={rx} ry={ry} fill={COLORS.muted} />
      {/* Intersection: highlight clipped */}
      <g clipPath="url(#venn-left)">
        <ellipse cx={cx + offset} cy={cy} rx={rx} ry={ry} fill={COLORS.highlight} />
      </g>
      {/* Outlines */}
      <ellipse cx={cx - offset} cy={cy} rx={rx} ry={ry} fill="none" stroke={COLORS.gold} strokeWidth="2.5" />
      <ellipse cx={cx + offset} cy={cy} rx={rx} ry={ry} fill="none" stroke={COLORS.gold} strokeWidth="2.5" />
      {/* Labels */}
      <MultilineText x={cx - offset - 40} y={cy} text={left} maxChars={14} lineHeight={22} font='700 19px "IBM Plex Sans", sans-serif' />
      <MultilineText x={cx + offset + 40} y={cy} text={right} maxChars={14} lineHeight={22} font='700 19px "IBM Plex Sans", sans-serif' />
      {intersection && (
        <MultilineText
          x={cx}
          y={cy}
          text={intersection}
          maxChars={14}
          lineHeight={18}
          font='700 16px "IBM Plex Sans", sans-serif'
          fill={COLORS.goldSoft}
        />
      )}
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* BARS (vertical bar chart with proportional fills)                   */
/* ------------------------------------------------------------------ */

export function BarsDiagram({ data }) {
  const categories = (data?.categories || []).slice(0, 6);
  const N = categories.length || 1;
  const W = Math.max(560, N * 200);
  const H = 480;
  const padTop = 30, padBottom = 60, padX = 60;
  const innerH = H - padTop - padBottom;
  const barW = Math.min(100, (W - padX * 2 - (N - 1) * 30) / N);
  const gap = (W - padX * 2 - N * barW) / Math.max(1, N - 1);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ maxWidth: "100%", height: "auto" }}>
      <line x1={padX - 12} y1={padTop} x2={padX - 12} y2={padTop + innerH} stroke={COLORS.axis} strokeWidth="1.25" />
      <line x1={padX - 12} y1={padTop + innerH} x2={W - padX + 12} y2={padTop + innerH} stroke={COLORS.axis} strokeWidth="1.25" />
      {categories.map((cat, i) => {
        const rawValue = Math.max(0, Math.min(1, Number(cat.value) || 0.5));
        const x = padX + i * (barW + gap);
        const fillH = innerH * rawValue;
        const emptyH = innerH - fillH;
        const fillY = padTop + emptyH;
        return (
          <g key={i}>
            <rect x={x} y={padTop} width={barW} height={emptyH} fill={COLORS.muted} stroke={COLORS.gold} strokeWidth="2" />
            <rect x={x} y={fillY} width={barW} height={fillH} fill={COLORS.highlight} stroke={COLORS.gold} strokeWidth="2" />
            <MultilineText
              x={x + barW / 2}
              y={padTop + innerH + 32}
              text={cat.label || ""}
              maxChars={14}
              lineHeight={16}
              font='500 14px "IBM Plex Sans", sans-serif'
              fill={COLORS.axisStrong}
            />
          </g>
        );
      })}
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* TIMELINE (horizontal line + numbered ovals, zigzag labels above/below) */
/* ------------------------------------------------------------------ */

export function TimelineDiagram({ data }) {
  const steps = (data?.steps || []).slice(0, 7);
  const N = steps.length || 1;
  const W = Math.max(720, N * 160);
  const H = 360;
  const cy = H / 2;
  const padX = 70;
  const nodeRx = 26, nodeRy = 36;
  const spacing = (W - padX * 2) / Math.max(1, N - 1);

  const positions = steps.map((_, i) => ({ x: padX + i * spacing, y: cy }));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ maxWidth: "100%", height: "auto" }}>
      {/* Horizontal connecting line — drawn from first to last node center */}
      <line
        x1={positions[0].x}
        y1={cy}
        x2={positions[positions.length - 1].x}
        y2={cy}
        stroke={COLORS.gold}
        strokeWidth="6"
      />
      {steps.map((label, i) => {
        const p = positions[i];
        const labelAbove = i % 2 === 0;
        const labelY = labelAbove ? cy - nodeRy - 36 : cy + nodeRy + 42;
        return (
          <g key={i}>
            {/* Numbered oval */}
            <ellipse cx={p.x} cy={p.y} rx={nodeRx} ry={nodeRy} fill={COLORS.highlight} stroke={COLORS.gold} strokeWidth="2.5" />
            <text x={p.x} y={p.y + 2} textAnchor="middle" dominantBaseline="middle" style={{ font: '700 18px "IBM Plex Sans", sans-serif', fill: COLORS.goldSoft }}>
              {i + 1}
            </text>
            {/* Label above or below */}
            <MultilineText
              x={p.x}
              y={labelY}
              text={label}
              maxChars={14}
              lineHeight={18}
              font='700 16px "IBM Plex Sans", sans-serif'
            />
          </g>
        );
      })}
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* DISPATCHER                                                          */
/* ------------------------------------------------------------------ */

const REGISTRY = {
  quadrant: QuadrantDiagram,
  triangle: TriangleDiagram,
  hub_spoke: HubSpokeDiagram,
  chain: ChainDiagram,
  tree: TreeDiagram,
  funnel: FunnelDiagram,
  layers: LayersDiagram,
  cycle: CycleDiagram,
  pyramid: PyramidDiagram,
  matrix: MatrixDiagram,
  venn: VennDiagram,
  bars: BarsDiagram,
  timeline: TimelineDiagram,
};

export default function DiagramBlock({ code }) {
  let spec = null;
  let parseError = null;
  try {
    spec = JSON.parse((code || "").trim());
  } catch (e) {
    parseError = String(e.message || e);
  }

  if (parseError || !spec || !REGISTRY[spec.type]) {
    return (
      <figure
        className="my-6 border border-ink-200 bg-ink-50 p-4 text-sm text-ink-600 font-mono whitespace-pre-wrap"
        data-testid="diagram-fallback"
      >
        {code}
      </figure>
    );
  }

  const Component = REGISTRY[spec.type];
  return (
    <figure
      className="my-8 border border-ink-200 bg-bone px-4 py-6 md:px-8 md:py-8 overflow-x-auto page-break-avoid"
      data-testid="diagram"
      data-diagram-type={spec.type}
    >
      <Component data={spec} />
    </figure>
  );
}
